
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Info, AlertCircle, XCircle } from 'lucide-react';
import { useDiagnostics } from './DiagnosticsContext';
import { InconsistencyData } from './DiagnosticsTypes';

const DiagnosticsTable: React.FC = () => {
  const { data, filters } = useDiagnostics();

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Фильтр по серьезности
      if (filters.selectedSeverity.length > 0 && !filters.selectedSeverity.includes(item.severity)) {
        return false;
      }

      // Фильтр по типу
      if (filters.selectedType.length > 0 && !filters.selectedType.includes(item.type)) {
        return false;
      }

      // Поиск по описанию
      if (filters.searchTerm && !item.description.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
        return false;
      }

      // Показать только критические проблемы
      if (filters.showOnlyIssues && !['critical', 'high'].includes(item.severity)) {
        return false;
      }

      return true;
    });
  }, [data, filters]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSeverityVariant = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getRentalStatusBadge = (status?: string) => {
    if (!status) return null;
    
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'active': 'default',
      'completed': 'secondary',
      'cancelled': 'destructive',
      'pending': 'outline'
    };

    const labels: Record<string, string> = {
      'active': 'Активная',
      'completed': 'Завершенная',
      'cancelled': 'Отмененная',
      'pending': 'Ожидает'
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="text-xs">
        {labels[status] || status}
      </Badge>
    );
  };

  const formatCurrency = (amount: number): string => {
    return `${amount.toLocaleString('ru-RU')}₽`;
  };

  const formatPercentage = (percentage: number): string => {
    return `${Math.round(percentage)}%`;
  };

  const formatDetailsForDisplay = (details: any) => {
    if (!details || typeof details !== 'object') return null;

    // Создаем красиво отформатированные детали
    const formattedDetails: { label: string; value: string }[] = [];

    // GPS тег (важная идентификационная информация)
    if (details.gpsTag) {
      formattedDetails.push({
        label: 'GPS тег',
        value: details.gpsTag
      });
    }

    // Период аренды
    if (details.rentalPeriod) {
      formattedDetails.push({
        label: 'Период аренды',
        value: details.rentalPeriod
      });
    }

    // Ожидаемая и фактическая сумма
    if (details.expectedAmount && details.actualAmount) {
      formattedDetails.push({
        label: 'Ожидаемая сумма',
        value: formatCurrency(details.expectedAmount)
      });
      formattedDetails.push({
        label: 'Фактическая сумма', 
        value: formatCurrency(details.actualAmount)
      });
    }

    // Разница в сумме
    if (details.difference) {
      formattedDetails.push({
        label: 'Разница',
        value: formatCurrency(details.difference)
      });
    }

    // Отклонение в процентах
    if (details.deviation) {
      formattedDetails.push({
        label: 'Отклонение',
        value: formatPercentage(details.deviation)
      });
    }

    // Влияние
    if (details.impact) {
      formattedDetails.push({
        label: 'Влияние',
        value: details.impact
      });
    }

    // Рекомендации
    if (details.suggestions && Array.isArray(details.suggestions)) {
      formattedDetails.push({
        label: 'Рекомендации',
        value: details.suggestions.join('; ')
      });
    }

    // Проблемы времени
    if (details.timingIssue) {
      formattedDetails.push({
        label: 'Проблема времени',
        value: details.timingIssue
      });
    }

    // Нарушение времени
    if (details.timeViolation) {
      formattedDetails.push({
        label: 'Нарушение времени',
        value: `${details.timeViolation} дней`
      });
    }

    // Задержка платежа
    if (details.paymentDelay) {
      formattedDetails.push({
        label: 'Задержка платежа',
        value: `${details.paymentDelay} дней`
      });
    }

    // Ожидаемое и фактическое состояние (для IoT и других проблем)
    if (details.expected && details.actual) {
      formattedDetails.push({
        label: 'Ожидается',
        value: details.expected
      });
      formattedDetails.push({
        label: 'Фактически',
        value: details.actual
      });
    }

    if (formattedDetails.length === 0) return null;

    return (
      <div className="text-xs text-muted-foreground mt-2 space-y-1">
        {formattedDetails.map((detail, index) => (
          <div key={index} className="flex gap-2">
            <span className="font-medium min-w-[120px]">{detail.label}:</span>
            <span className="flex-1">{detail.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Обнаруженные несоответствия ({filteredData.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Серьезность</TableHead>
                <TableHead className="w-32">Тип</TableHead>
                <TableHead className="min-w-[300px]">Описание</TableHead>
                <TableHead className="w-28">Статус аренды</TableHead>
                <TableHead className="w-40">Период аренды</TableHead>
                <TableHead className="w-28">Объект</TableHead>
                <TableHead className="w-32">Время</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Несоответствия не найдены
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow key={item.id} className="hover:bg-muted/50">
                    <TableCell className="align-top">
                      <div className="flex items-center gap-2">
                        {getSeverityIcon(item.severity)}
                        <Badge variant={getSeverityVariant(item.severity)} className="text-xs whitespace-nowrap">
                          {item.severity === 'critical' ? 'Критическая' :
                           item.severity === 'high' ? 'Высокая' :
                           item.severity === 'medium' ? 'Средняя' : 'Низкая'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="outline" className="text-xs whitespace-nowrap">
                        {item.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-2">
                        <div className="text-sm font-medium leading-relaxed">
                          {item.description}
                        </div>
                        {formatDetailsForDisplay(item.details)}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      {getRentalStatusBadge(item.rentalStatus)}
                    </TableCell>
                    <TableCell className="text-xs align-top">
                      <div className="whitespace-nowrap">
                        {item.rentalPeriodDisplay || 'Не указан'}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">
                        {item.affectedEntity === 'bike' ? 'Велосипед' :
                         item.affectedEntity === 'rental' ? 'Аренда' :
                         item.affectedEntity === 'courier' ? 'Курьер' :
                         item.affectedEntity === 'payment' ? 'Платеж' : 'Система'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground align-top">
                      <div className="whitespace-nowrap">
                        {new Date(item.timestamp).toLocaleString('ru-RU')}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default DiagnosticsTable;
